/**
 * Syllabus expansion — LH depth + both-hands independence pieces.
 * Imported into BUILTIN_LIBRARY.
 */

import type { HandId, MusicPiece, NoteEvent } from "./types.js";

function melody(
  events: Array<[number, number, number, number?]>,
  hand: HandId = "right",
): NoteEvent[] {
  return events.map(([beat, duration, midi, finger]) => ({
    beat,
    duration,
    midi,
    velocity: 92,
    hand,
    finger: finger as 1 | 2 | 3 | 4 | 5 | undefined,
  }));
}

function sequence(
  midis: number[],
  beatStep: number,
  hand: HandId = "right",
  startBeat = 0,
): NoteEvent[] {
  return midis.map((midi, i) => ({
    beat: startBeat + i * beatStep,
    duration: beatStep,
    midi,
    velocity: 90,
    hand,
  }));
}

function piece(
  partial: Omit<MusicPiece, "notes"> & { notes: NoteEvent[] },
): MusicPiece {
  return partial;
}

/** Same-beat LH + RH pairs (followed as chords by FollowAlong). */
function handsTogether(
  pairs: Array<{ beat: number; lh: number; rh: number; dur?: number }>,
): NoteEvent[] {
  const notes: NoteEvent[] = [];
  for (const p of pairs) {
    const d = p.dur ?? 1;
    notes.push({ beat: p.beat, duration: d, midi: p.lh, velocity: 85, hand: "left" });
    notes.push({ beat: p.beat, duration: d, midi: p.rh, velocity: 92, hand: "right" });
  }
  return notes;
}

export const EXPANSION_PIECES: MusicPiece[] = [
  piece({
    id: "ex-five-finger-g-lh",
    title: "Five-Finger Drill (G Position LH)",
    category: "exercise",
    bpm: 60,
    timeSignature: "4/4",
    key: "G major",
    hand: "left",
    stage: 1,
    description: "LH five-finger on G3–D4.",
    notes: melody(
      [
        [0, 1, 55, 5],
        [1, 1, 57, 4],
        [2, 1, 59, 3],
        [3, 1, 60, 2],
        [4, 1, 62, 1],
        [5, 1, 60, 2],
        [6, 1, 59, 3],
        [7, 1, 57, 4],
        [8, 2, 55, 5],
      ],
      "left",
    ),
  }),
  piece({
    id: "scale-f-major-lh",
    title: "F Major Scale (LH 1 oct)",
    category: "scale",
    bpm: 72,
    timeSignature: "4/4",
    key: "F major",
    hand: "left",
    stage: 2,
    description: "LH F major — introduces Bb attention for later RH pairing.",
    notes: sequence([53, 55, 57, 58, 60, 62, 64, 65, 64, 62, 60, 58, 57, 55, 53], 0.5, "left"),
  }),
  piece({
    id: "scale-d-major-lh",
    title: "D Major Scale (LH 1 oct)",
    category: "scale",
    bpm: 72,
    timeSignature: "4/4",
    key: "D major",
    hand: "left",
    stage: 2,
    description: "LH D major with F# / C#.",
    notes: sequence([50, 52, 54, 55, 57, 59, 61, 62, 61, 59, 57, 55, 54, 52, 50], 0.5, "left"),
  }),
  piece({
    id: "lh-alberti-c",
    title: "LH Alberti Bass (C)",
    category: "exercise",
    bpm: 80,
    timeSignature: "4/4",
    key: "C major",
    hand: "left",
    stage: 3,
    description: "Classic Alberti pattern: low–high–mid–high.",
    notes: sequence(
      [48, 55, 52, 55, 48, 55, 52, 55, 48, 55, 52, 55, 48, 55, 52, 55],
      0.5,
      "left",
    ),
  }),
  piece({
    id: "lh-chord-root-fifth",
    title: "LH Root–Fifth March",
    category: "exercise",
    bpm: 76,
    timeSignature: "4/4",
    key: "C major",
    hand: "left",
    stage: 2,
    description: "LH accompaniment seed: C–G power pattern.",
    notes: melody(
      [
        [0, 1, 48],
        [1, 1, 55],
        [2, 1, 48],
        [3, 1, 55],
        [4, 1, 50],
        [5, 1, 57],
        [6, 1, 50],
        [7, 1, 57],
        [8, 1, 48],
        [9, 1, 55],
        [10, 2, 48],
      ],
      "left",
    ),
  }),
  piece({
    id: "both-c-position-mirrors",
    title: "Both Hands Mirror (C Position)",
    category: "exercise",
    bpm: 66,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 1,
    description: "Play LH + RH same finger-number on mirrored C positions together.",
    notes: handsTogether([
      { beat: 0, lh: 48, rh: 60 },
      { beat: 1, lh: 50, rh: 62 },
      { beat: 2, lh: 52, rh: 64 },
      { beat: 3, lh: 53, rh: 65 },
      { beat: 4, lh: 55, rh: 67 },
      { beat: 5, lh: 53, rh: 65 },
      { beat: 6, lh: 52, rh: 64 },
      { beat: 7, lh: 50, rh: 62 },
      { beat: 8, lh: 48, rh: 60, dur: 2 },
    ]),
  }),
  piece({
    id: "both-contrary-c",
    title: "Contrary Motion (C)",
    category: "exercise",
    bpm: 70,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 2,
    description: "Hands move opposite directions from middle C / C3.",
    notes: handsTogether([
      { beat: 0, lh: 48, rh: 60 },
      { beat: 1, lh: 47, rh: 62 },
      { beat: 2, lh: 45, rh: 64 },
      { beat: 3, lh: 43, rh: 65 },
      { beat: 4, lh: 41, rh: 67 },
      { beat: 5, lh: 43, rh: 65 },
      { beat: 6, lh: 45, rh: 64 },
      { beat: 7, lh: 47, rh: 62 },
      { beat: 8, lh: 48, rh: 60, dur: 2 },
    ]),
  }),
  piece({
    id: "both-parallel-thirds",
    title: "Parallel 3rds (Both Hands)",
    category: "exercise",
    bpm: 72,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 3,
    description: "Same-beat intervals a third apart across hands.",
    notes: handsTogether([
      { beat: 0, lh: 48, rh: 64 },
      { beat: 1, lh: 50, rh: 65 },
      { beat: 2, lh: 52, rh: 67 },
      { beat: 3, lh: 53, rh: 69 },
      { beat: 4, lh: 55, rh: 71 },
      { beat: 5, lh: 53, rh: 69 },
      { beat: 6, lh: 52, rh: 67 },
      { beat: 7, lh: 50, rh: 65 },
      { beat: 8, lh: 48, rh: 64, dur: 2 },
    ]),
  }),
  piece({
    id: "both-ode-with-bass",
    title: "Ode Theme + LH Root Bass",
    category: "melody",
    bpm: 84,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 3,
    description: "RH Ode fragment over LH C–G bass on beats.",
    notes: [
      { beat: 0, duration: 1, midi: 48, velocity: 80, hand: "left" },
      { beat: 0, duration: 1, midi: 64, velocity: 92, hand: "right" },
      { beat: 1, duration: 1, midi: 64, velocity: 92, hand: "right" },
      { beat: 2, duration: 1, midi: 55, velocity: 80, hand: "left" },
      { beat: 2, duration: 1, midi: 65, velocity: 92, hand: "right" },
      { beat: 3, duration: 1, midi: 67, velocity: 92, hand: "right" },
      { beat: 4, duration: 1, midi: 48, velocity: 80, hand: "left" },
      { beat: 4, duration: 1, midi: 67, velocity: 92, hand: "right" },
      { beat: 5, duration: 1, midi: 65, velocity: 92, hand: "right" },
      { beat: 6, duration: 1, midi: 55, velocity: 80, hand: "left" },
      { beat: 6, duration: 1, midi: 64, velocity: 92, hand: "right" },
      { beat: 7, duration: 1, midi: 62, velocity: 92, hand: "right" },
      { beat: 8, duration: 2, midi: 48, velocity: 80, hand: "left" },
      { beat: 8, duration: 2, midi: 60, velocity: 92, hand: "right" },
    ],
  }),
  piece({
    id: "both-block-i-v",
    title: "Both Hands Block I–V",
    category: "chord",
    bpm: 70,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 3,
    description: "LH bass + RH triad hits on I and V.",
    notes: [
      { beat: 0, duration: 2, midi: 48, velocity: 85, hand: "left" },
      { beat: 0, duration: 2, midi: 60, velocity: 90, hand: "right" },
      { beat: 0, duration: 2, midi: 64, velocity: 90, hand: "right" },
      { beat: 0, duration: 2, midi: 67, velocity: 90, hand: "right" },
      { beat: 2, duration: 2, midi: 55, velocity: 85, hand: "left" },
      { beat: 2, duration: 2, midi: 59, velocity: 90, hand: "right" },
      { beat: 2, duration: 2, midi: 62, velocity: 90, hand: "right" },
      { beat: 2, duration: 2, midi: 67, velocity: 90, hand: "right" },
      { beat: 4, duration: 2, midi: 48, velocity: 85, hand: "left" },
      { beat: 4, duration: 2, midi: 60, velocity: 90, hand: "right" },
      { beat: 4, duration: 2, midi: 64, velocity: 90, hand: "right" },
      { beat: 4, duration: 2, midi: 67, velocity: 90, hand: "right" },
      { beat: 6, duration: 2, midi: 48, velocity: 85, hand: "left" },
      { beat: 6, duration: 2, midi: 60, velocity: 95, hand: "right" },
    ],
  }),
  piece({
    id: "both-scale-contrary-1oct",
    title: "C Scale Contrary (Both)",
    category: "scale",
    bpm: 76,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 4,
    description: "One-octave C major — RH up while LH down (and return).",
    notes: handsTogether([
      { beat: 0, lh: 60, rh: 60, dur: 0.5 },
      { beat: 0.5, lh: 59, rh: 62, dur: 0.5 },
      { beat: 1, lh: 57, rh: 64, dur: 0.5 },
      { beat: 1.5, lh: 55, rh: 65, dur: 0.5 },
      { beat: 2, lh: 53, rh: 67, dur: 0.5 },
      { beat: 2.5, lh: 52, rh: 69, dur: 0.5 },
      { beat: 3, lh: 50, rh: 71, dur: 0.5 },
      { beat: 3.5, lh: 48, rh: 72, dur: 0.5 },
      { beat: 4, lh: 50, rh: 71, dur: 0.5 },
      { beat: 4.5, lh: 52, rh: 69, dur: 0.5 },
      { beat: 5, lh: 53, rh: 67, dur: 0.5 },
      { beat: 5.5, lh: 55, rh: 65, dur: 0.5 },
      { beat: 6, lh: 57, rh: 64, dur: 0.5 },
      { beat: 6.5, lh: 59, rh: 62, dur: 0.5 },
      { beat: 7, lh: 60, rh: 60, dur: 1 },
    ]),
  }),
  piece({
    id: "both-twinkle-duet",
    title: "Twinkle Hands-Together Seed",
    category: "melody",
    bpm: 80,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 2,
    description: "Simplified Twinkle RH with LH tonic hits on strong beats.",
    notes: [
      { beat: 0, duration: 1, midi: 48, velocity: 78, hand: "left" },
      { beat: 0, duration: 1, midi: 60, velocity: 92, hand: "right" },
      { beat: 1, duration: 1, midi: 60, velocity: 92, hand: "right" },
      { beat: 2, duration: 1, midi: 55, velocity: 78, hand: "left" },
      { beat: 2, duration: 1, midi: 67, velocity: 92, hand: "right" },
      { beat: 3, duration: 1, midi: 67, velocity: 92, hand: "right" },
      { beat: 4, duration: 1, midi: 48, velocity: 78, hand: "left" },
      { beat: 4, duration: 1, midi: 69, velocity: 92, hand: "right" },
      { beat: 5, duration: 1, midi: 69, velocity: 92, hand: "right" },
      { beat: 6, duration: 2, midi: 55, velocity: 78, hand: "left" },
      { beat: 6, duration: 2, midi: 67, velocity: 92, hand: "right" },
    ],
  }),
  piece({
    id: "both-sight-duet-lite",
    title: "Sight Duet Lite",
    category: "melody",
    bpm: 88,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 4,
    description: "Short hands-together reading for Stage 4.",
    notes: handsTogether([
      { beat: 0, lh: 48, rh: 64 },
      { beat: 1, lh: 48, rh: 65 },
      { beat: 2, lh: 55, rh: 67 },
      { beat: 3, lh: 55, rh: 65 },
      { beat: 4, lh: 48, rh: 64 },
      { beat: 5, lh: 52, rh: 62 },
      { beat: 6, lh: 48, rh: 60, dur: 2 },
    ]),
  }),
  piece({
    id: "both-speed-alternation",
    title: "Fast Hand Alternation",
    category: "exercise",
    bpm: 100,
    timeSignature: "4/4",
    key: "C major",
    hand: "both",
    stage: 5,
    description: "Elite tempo LH–RH alternation.",
    notes: [
      { beat: 0, duration: 0.5, midi: 48, velocity: 88, hand: "left" },
      { beat: 0.5, duration: 0.5, midi: 64, velocity: 90, hand: "right" },
      { beat: 1, duration: 0.5, midi: 52, velocity: 88, hand: "left" },
      { beat: 1.5, duration: 0.5, midi: 67, velocity: 90, hand: "right" },
      { beat: 2, duration: 0.5, midi: 55, velocity: 88, hand: "left" },
      { beat: 2.5, duration: 0.5, midi: 72, velocity: 90, hand: "right" },
      { beat: 3, duration: 0.5, midi: 52, velocity: 88, hand: "left" },
      { beat: 3.5, duration: 0.5, midi: 67, velocity: 90, hand: "right" },
      { beat: 4, duration: 0.5, midi: 48, velocity: 88, hand: "left" },
      { beat: 4.5, duration: 0.5, midi: 64, velocity: 90, hand: "right" },
      { beat: 5, duration: 0.5, midi: 48, velocity: 88, hand: "left" },
      { beat: 5.5, duration: 0.5, midi: 60, velocity: 95, hand: "right" },
    ],
  }),
  piece({
    id: "ex-black-keys-lh",
    title: "Black-Key Clusters (LH)",
    category: "exercise",
    bpm: 68,
    timeSignature: "4/4",
    key: "C major",
    hand: "left",
    stage: 2,
    description: "LH explores F# / G# / A# shapes.",
    notes: sequence([54, 56, 58, 56, 54, 56, 58, 61, 58, 56, 54], 0.5, "left"),
  }),
  piece({
    id: "melody-jingle-rh",
    title: "Jingle Bells (RH excerpt)",
    category: "melody",
    bpm: 100,
    timeSignature: "4/4",
    key: "C major",
    hand: "right",
    stage: 1,
    description: "Familiar tune for reading confidence.",
    notes: melody([
      [0, 1, 64],
      [1, 1, 64],
      [2, 2, 64],
      [4, 1, 64],
      [5, 1, 64],
      [6, 2, 64],
      [8, 1, 64],
      [9, 1, 67],
      [10, 1, 60],
      [11, 1, 62],
      [12, 4, 64],
    ]),
  }),
];
